import type { CustomerFileAccessDto, CustomerFileUserDto } from '@cpm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ResourceConsoleCommandResultDto,
  ResourceConsoleOperation,
  ResourceConsoleSessionDto,
  SftpCredentialDto,
  SftpUserDto,
  SubscriptionFileAccessDto,
} from '@cpm/shared';
import { formatLimitAmount, RESOURCE_CONSOLE_OPERATIONS } from '@cpm/shared';
import { Check, Copy, KeyRound, Power, ShieldOff, Terminal, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { Modal } from './Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Spinner,
  type BadgeTone,
} from './ui.js';

function accessTone(status: string): BadgeTone {
  if (status === 'active') return 'green';
  if (status === 'suspended' || status === 'expired') return 'yellow';
  return 'gray';
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
    >
      {copied ? <Check className="size-3.5 text-green-600" /> : <Copy className="size-3.5" />}
    </button>
  );
}

function CredentialModal({
  credential,
  onClose,
}: {
  credential: SftpCredentialDto;
  onClose: () => void;
}) {
  const rows: Array<[string, string | null]> = [
    ['Username', credential.username],
    ['Password', credential.password],
    ['SFTP host', credential.sftpHost],
    ['SFTP port', credential.sftpPort != null ? String(credential.sftpPort) : null],
    ['WebClient', credential.webClientUrl],
    ['Expires', credential.expiresAt],
  ];
  return (
    <Modal title="SFTP credentials" open onClose={onClose}>
      <p className="mb-3 text-sm text-amber-600 dark:text-amber-400">
        Copy the password now — it is shown once and cannot be retrieved later.
      </p>
      <dl className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
        {rows.map(([label, value]) =>
          value == null ? null : (
            <div key={label} className="flex items-center gap-2 py-2">
              <dt className="w-28 shrink-0 text-slate-500 dark:text-slate-400">{label}</dt>
              <dd className="break-all font-mono text-xs">{value}</dd>
              <span className="ml-auto">
                <CopyButton text={value} label={`Copy ${label}`} />
              </span>
            </div>
          ),
        )}
      </dl>
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

function AccountCard({
  subscriptionId,
  user,
  isAdmin,
  onCredential,
  onChanged,
  onEdit,
}: {
  onEdit: (user: CustomerFileUserDto) => void;
  subscriptionId: string;
  user: CustomerFileUserDto;
  isAdmin: boolean;
  onCredential: (credential: SftpCredentialDto) => void;
  onChanged: () => void;
}) {
  const reset = useMutation({
    mutationFn: () =>
      api.post<SftpCredentialDto>(
        `/api/files/subscriptions/${subscriptionId}/users/${user.id}/reset-password`,
        {},
      ),
    onSuccess: (credential) => {
      onCredential(credential);
      onChanged();
    },
  });
  const toggleReadOnly = useMutation({
    mutationFn: (readOnly: boolean) =>
      api.patch(`/api/files/subscriptions/${subscriptionId}/users/${user.id}`, { readOnly }),
    onSuccess: onChanged,
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) =>
      api.post(
        `/api/files/subscriptions/${subscriptionId}/users/${user.id}/${enabled ? 'enable' : 'disable'}`,
        {},
      ),
    onSuccess: onChanged,
  });
  const deprovision = useMutation({
    mutationFn: () =>
      api.delete(`/api/files/subscriptions/${subscriptionId}/users/${user.id}`),
    onSuccess: onChanged,
  });
  const error = reset.error ?? toggleReadOnly.error ?? setEnabled.error ?? deprovision.error;

  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{user.label}</span>
        <Badge tone="gray">{user.accountType}</Badge>
        <Badge tone={accessTone(user.status)}>{user.status}</Badge>
        {user.readOnly && <Badge tone="gray">read-only</Badge>}
        <span className="ml-auto font-mono text-xs">{user.username}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        {user.sftpHost && <span>SFTP {user.sftpHost}{user.sftpPort ? `:${user.sftpPort}` : ''}</span>}
        <span>quota {formatLimitAmount(user.quotaMb, 'megabytes')}</span>
        <span>sessions {user.maxSessions}</span>
        <span>{user.expiresAt ? `expires ${new Date(user.expiresAt).toLocaleString()}` : 'no expiry'}</span>
        <span>
          {user.allowedIps.length + user.deniedIps.length} IP restriction
          {user.allowedIps.length + user.deniedIps.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="mt-3 divide-y divide-slate-100 text-sm dark:divide-slate-800">
        {user.folders.map((folder) => (
          <li key={folder.virtualPath} className="flex flex-wrap items-center gap-2 py-1.5">
            <span className="font-mono text-xs">{folder.virtualPath}</span>
            <Badge tone={folder.mode === 'ro' ? 'gray' : 'green'}>{folder.mode}</Badge>
            <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
              {folder.resourceName} · {folder.relativePath}
            </span>
          </li>
        ))}
      </ul>
      {error && <div className="mt-3"><ErrorNote error={error} /></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        {user.status === 'active' && user.webClientUrl && <a href={user.webClientUrl} target="_blank" rel="noopener noreferrer"><Button variant="secondary">Open file manager</Button></a>}
        <Button variant="secondary" onClick={() => onEdit(user)}>Edit account</Button>
        <Button variant="secondary" loading={reset.isPending} onClick={() => reset.mutate()}>
          <KeyRound className="mr-1 size-4" /> Rotate credential
        </Button>
        <Button
          variant="secondary"
          loading={toggleReadOnly.isPending}
          onClick={() => toggleReadOnly.mutate(!user.readOnly)}
        >
          {user.readOnly ? 'Make read-write' : 'Make read-only'}
        </Button>
        {user.status === 'active' ? (
          <Button
            variant="secondary"
            loading={setEnabled.isPending}
            onClick={() => setEnabled.mutate(false)}
          >
            <Power className="mr-1 size-4" /> Disable
          </Button>
        ) : user.status === 'disabled' ? (
          <Button
            variant="secondary"
            loading={setEnabled.isPending}
            onClick={() => setEnabled.mutate(true)}
          >
            <Power className="mr-1 size-4" /> Enable
          </Button>
        ) : null}
        {isAdmin && (
          <Button
            variant="danger"
            loading={deprovision.isPending}
            onClick={() => {
              if (confirm(`Delete ${user.label} and its SFTP credential?`)) deprovision.mutate();
            }}
          >
            <Trash2 className="mr-1 size-4" /> Delete grant
          </Button>
        )}
      </div>
    </div>
  );
}

function ResourceConsole({
  data,
  onChanged,
}: {
  data: Pick<SubscriptionFileAccessDto, 'consoleEnabled' | 'consoleResources'>;
  onChanged: () => void;
}) {
  const [resourceId, setResourceId] = useState(data.consoleResources[0]?.id ?? '');
  const [session, setSession] = useState<ResourceConsoleSessionDto | null>(null);
  const [operation, setOperation] =
    useState<ResourceConsoleOperation>('working-directory');
  const [path, setPath] = useState('.');
  const [result, setResult] = useState<ResourceConsoleCommandResultDto | null>(null);
  const start = useMutation({
    mutationFn: () =>
      api.post<ResourceConsoleSessionDto>(`/api/files/resources/${resourceId}/console/sessions`, {
        durationMinutes: 15,
      }),
    onSuccess: (value) => {
      setSession(value);
      setResult(null);
    },
  });
  const run = useMutation({
    mutationFn: () =>
      api.post<ResourceConsoleCommandResultDto>(
        `/api/files/console/sessions/${session!.id}/commands`,
        {
          operation,
          ...(operation === 'list-files' || operation === 'disk-usage' ? { path } : {}),
        },
      ),
    onSuccess: setResult,
  });
  const stop = useMutation({
    mutationFn: () => api.delete(`/api/files/console/sessions/${session!.id}`),
    onSuccess: () => {
      setSession(null);
      onChanged();
    },
  });
  if (!data.consoleEnabled) return null;
  return (
    <Card>
      <CardHeader
        title="Resource console"
        description="Short-lived, audited operations inside one selected container; no shell or host access"
      />
      <div className="space-y-3 px-5 pb-5">
        {!session ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-64 text-sm">
              <span className="mb-1 block text-xs text-slate-500">Resource</span>
              <select
                className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                value={resourceId}
                onChange={(event) => setResourceId(event.target.value)}
              >
                {data.consoleResources.map((resource) => (
                  <option key={resource.id} value={resource.id}>
                    {resource.name} ({resource.kind})
                  </option>
                ))}
              </select>
            </label>
            <Button
              disabled={!resourceId}
              loading={start.isPending}
              onClick={() => start.mutate()}
            >
              <Terminal className="mr-1 size-4" /> Start 15-minute session
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone="green">active</Badge>
              <span>{session.resourceName}</span>
              <span className="text-xs text-slate-500">
                expires {new Date(session.expiresAt).toLocaleTimeString()} · {session.commandCount}/
                {session.maxCommands} commands
              </span>
              <Button
                variant="secondary"
                className="ml-auto"
                loading={stop.isPending}
                onClick={() => stop.mutate()}
              >
                Stop
              </Button>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="mb-1 block text-xs text-slate-500">Operation</span>
                <select
                  className="rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                  value={operation}
                  onChange={(event) => setOperation(event.target.value as ResourceConsoleOperation)}
                >
                  {RESOURCE_CONSOLE_OPERATIONS.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              {(operation === 'list-files' || operation === 'disk-usage') && (
                <label className="min-w-48 text-sm">
                  <span className="mb-1 block text-xs text-slate-500">Relative path</span>
                  <input
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-700 dark:bg-slate-950"
                    value={path}
                    onChange={(event) => setPath(event.target.value)}
                  />
                </label>
              )}
              <Button loading={run.isPending} onClick={() => run.mutate()}>Run</Button>
            </div>
            {result && (
              <pre className="max-h-72 overflow-auto rounded bg-slate-950 p-3 text-xs text-slate-100">
                {result.output || `(exit ${result.exitCode}, no output)`}
              </pre>
            )}
          </>
        )}
        {(start.error ?? run.error ?? stop.error) && (
          <ErrorNote error={start.error ?? run.error ?? stop.error} />
        )}
      </div>
    </Card>
  );
}

export function SubscriptionFilesPanel({ subscriptionId, customer = false }: { subscriptionId: string; customer?: boolean }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const [credential, setCredential] = useState<SftpCredentialDto | null>(null);
  const [label, setLabel] = useState('File access');
  const [editing, setEditing] = useState<string | null>(null);
  const [accountType, setAccountType] = useState<'tenant' | 'service'>('tenant');
  const [readOnly, setReadOnly] = useState(false);
  const [expiry, setExpiry] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [deniedIps, setDeniedIps] = useState('');
  const [quotaMb, setQuotaMb] = useState('');
  const [maxSessions, setMaxSessions] = useState('2');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [folderPaths, setFolderPaths] = useState<Record<string, string>>({});
  const [folderModes, setFolderModes] = useState<Record<string, 'ro' | 'rw'>>({});
  const query = useQuery({
    queryKey: ['files', 'subscription', subscriptionId],
    queryFn: () =>
      api.get<SubscriptionFileAccessDto | CustomerFileAccessDto>(`/api/files/subscriptions/${subscriptionId}`),
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['files', 'subscription', subscriptionId] });
    void queryClient.invalidateQueries({ queryKey: ['portal', 'subscriptions', subscriptionId] });
  };
  const folderByKey = useMemo(
    () =>
      new Map(
        (query.data?.availableFolders ?? []).map((folder) => [
          `${folder.resourceId}\0${folder.volumeName}`,
          folder,
        ]),
      ),
    [query.data?.availableFolders],
  );
  const provision = useMutation({
    mutationFn: async () => {
      const input = {
        label,
        accountType,
        // Explicit folder modes carry edit intent. Sending readOnly:false on
        // an edit would invoke the API's separate make-all-read-write action.
        ...(editing ? {} : { readOnly }),
        folders: [...selected].map((key) => {
          const folder = folderByKey.get(key);
          if (!folder) throw new Error('A selected folder is no longer available. Refresh and select current folders.');
          return {
            resourceId: folder.resourceId,
            volumeName: folder.volumeName,
            relativePath: folderPaths[key]?.trim() || '.',
            mode: readOnly ? 'ro' : (folderModes[key] ?? 'rw'),
          };
        }),
        ...(quotaMb ? { quotaMb: Number(quotaMb) } : editing ? { quotaMb: null } : {}),
        expiresAt: expiry ? new Date(expiry).toISOString() : null,
        allowedIps: allowedIps.split(/[\s,]+/).filter(Boolean),
        deniedIps: deniedIps.split(/[\s,]+/).filter(Boolean),
        maxSessions: Number(maxSessions),
      };
      if (editing) { await api.patch(`/api/files/subscriptions/${subscriptionId}/users/${editing}`, input); return null; }
      return api.post<SftpCredentialDto>(`/api/files/subscriptions/${subscriptionId}/provision`, input);
    },
    onSuccess: (value) => {
      setCredential(value);
      setEditing(null);
      setSelected(new Set());
      setFolderPaths({});
      setFolderModes({});
      invalidate();
    },
  });
  const revokeAll = useMutation({
    mutationFn: () => api.post(`/api/files/subscriptions/${subscriptionId}/revoke-all`, {}),
    onSuccess: invalidate,
  });

  if (query.isPending) return <Spinner />;
  if (query.error) return <ErrorNote error={query.error} />;
  const data = query.data;
  if (!data.featureEnabled && data.users.length === 0 && !data.consoleEnabled) return null;
  const canAdd =
    data.featureEnabled &&
    data.configured &&
    data.availableFolders.length > 0 &&
    selected.size > 0 &&
    (editing !== null || data.limit == null || data.used < data.limit);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="File access"
          description="Isolated SFTP/WebClient grants for selected resource volumes"
        />
        <div className="space-y-4 px-5 pb-5">
          {!data.featureEnabled && (
            <p className="text-sm text-slate-500">File access is not included in this plan.</p>
          )}
          {!data.configured && (
            <p className="text-sm text-amber-600">Your provider must finish configuring file access.</p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>{data.used}/{data.limit ?? '∞'} accounts</span>
            {!customer && data.users.length > 0 && (
              <Button
                variant="danger"
                loading={revokeAll.isPending}
                onClick={() => {
                  if (confirm('Disable every SFTP grant and revoke every console session now?')) {
                    revokeAll.mutate();
                  }
                }}
              >
                <ShieldOff className="mr-1 size-4" /> Revoke all access
              </Button>
            )}
          </div>
          {data.users.map((user) => (
            <AccountCard
              key={user.id}
              subscriptionId={subscriptionId}
              user={user}
              isAdmin={customer || me.data?.role === 'admin'}
              onCredential={setCredential}
              onChanged={invalidate}
              onEdit={(user) => {
                setEditing(user.id); setLabel(user.label); setAccountType(user.accountType); setReadOnly(user.readOnly);
                setQuotaMb(user.quotaMb == null ? '' : String(user.quotaMb)); setMaxSessions(String(user.maxSessions));
                setExpiry(user.expiresAt ? new Date(new Date(user.expiresAt).getTime() - new Date(user.expiresAt).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
                setAllowedIps(user.allowedIps.join(', ')); setDeniedIps(user.deniedIps.join(', '));
                setSelected(new Set(user.folders.map((f) => `${f.resourceId}\0${f.volumeName}`)));
                setFolderPaths(Object.fromEntries(user.folders.map((f) => [`${f.resourceId}\0${f.volumeName}`, f.relativePath])));
                setFolderModes(Object.fromEntries(user.folders.map((f) => [`${f.resourceId}\0${f.volumeName}`, f.mode])));
              }}
            />
          ))}
          {data.featureEnabled && (
            <div className="rounded-lg border border-dashed border-slate-300 p-4 dark:border-slate-700">
              <p className="mb-3 text-sm font-medium">{editing ? 'Edit file account' : 'Add isolated account'}</p>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">Label</span>
                  <input
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">Account type</span>
                  <select
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                    value={accountType}
                    onChange={(event) => setAccountType(event.target.value as 'tenant' | 'service')}
                  >
                    <option value="tenant">Tenant + WebClient</option>
                    <option value="service">Service (SFTP only)</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">Expiry (optional)</span>
                  <input
                    type="datetime-local"
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                    value={expiry}
                    onChange={(event) => setExpiry(event.target.value)}
                  />
                </label>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">
                    Allowed IP networks (CIDR, optional)
                  </span>
                  <input
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-700 dark:bg-slate-950"
                    placeholder="203.0.113.0/24, 2001:db8::/32"
                    value={allowedIps}
                    onChange={(event) => setAllowedIps(event.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">
                    Denied IP networks (CIDR, optional)
                  </span>
                  <input
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-700 dark:bg-slate-950"
                    placeholder="203.0.113.44/32"
                    value={deniedIps}
                    onChange={(event) => setDeniedIps(event.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">
                    {editing ? 'Account quota MB (blank = unlimited, if allowed)' : 'Account quota MB (blank uses plan disk limit)'}
                  </span>
                  <input
                    type="number"
                    min={1}
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                    value={quotaMb}
                    onChange={(event) => setQuotaMb(event.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs text-slate-500">Concurrent sessions</span>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950"
                    value={maxSessions}
                    onChange={(event) => setMaxSessions(event.target.value)}
                  />
                </label>
              </div>
              <div className="mt-3 space-y-1">
                {data.availableFolders.map((folder) => {
                  const key = `${folder.resourceId}\0${folder.volumeName}`;
                  return (
                    <div
                      key={key}
                      className="grid items-center gap-2 rounded border border-slate-200 p-2 text-sm dark:border-slate-800 md:grid-cols-[auto_1fr_1fr_auto]"
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select ${folder.resourceName} ${folder.volumeName}`}
                        checked={selected.has(key)}
                        onChange={(event) => {
                          setSelected((previous) => {
                            const next = new Set(previous);
                            if (event.target.checked) next.add(key);
                            else next.delete(key);
                            return next;
                          });
                        }}
                      />
                      <span>
                        {folder.resourceName}{' '}
                        <span className="font-mono text-xs text-slate-500">
                          {'label' in folder ? String(folder.label) : folder.volumeName}
                        </span>
                      </span>
                      <input
                        aria-label={`Relative path for ${folder.resourceName} ${folder.volumeName}`}
                        className="rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs dark:border-slate-700 dark:bg-slate-950"
                        placeholder=". or uploads/public"
                        disabled={!selected.has(key)}
                        value={folderPaths[key] ?? '.'}
                        onChange={(event) =>
                          setFolderPaths((previous) => ({
                            ...previous,
                            [key]: event.target.value,
                          }))
                        }
                      />
                      <select
                        aria-label={`Access mode for ${folder.resourceName} ${folder.volumeName}`}
                        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-950"
                        disabled={!selected.has(key) || readOnly}
                        value={readOnly ? 'ro' : (folderModes[key] ?? 'rw')}
                        onChange={(event) =>
                          setFolderModes((previous) => ({
                            ...previous,
                            [key]: event.target.value as 'ro' | 'rw',
                          }))
                        }
                      >
                        <option value="rw">read-write</option>
                        <option value="ro">read-only</option>
                      </select>
                    </div>
                  );
                })}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={readOnly}
                  onChange={(event) => setReadOnly(event.target.checked)}
                />
                Make every selected folder read-only
              </label>
              {editing && <Button variant="ghost" onClick={() => { setEditing(null); setSelected(new Set()); }}>Cancel editing</Button>}
              {provision.isSuccess && !credential && <p role="status">Account settings saved.</p>}
              {provision.error && <div className="mt-3"><ErrorNote error={provision.error} /></div>}
              <Button
                className="mt-3"
                disabled={!canAdd}
                loading={provision.isPending}
                onClick={() => provision.mutate()}
              >
                {editing ? 'Save account' : 'Add account'}
              </Button>
            </div>
          )}
          {revokeAll.error && <ErrorNote error={revokeAll.error} />}
        </div>
      </Card>
      <ResourceConsole data={data} onChanged={invalidate} />
      {credential && (
        <CredentialModal credential={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}
